using "./dom";
using "./component";
using "./velement";

// History-API routing (real paths — `/about`, not `#/about`) via
// `history.pushState`/the browser's native `popstate` event. This needs a
// server that falls back to the app shell for any path it doesn't
// recognize as a real file (see KopularDemo's scripts/serve.mjs) — a plain
// static file server has nothing to serve at `/about` on a direct
// visit/refresh otherwise, unlike hash-based routing, which never leaves
// the one HTML file the server actually has. Route registration is
// imperative (`AddRoute` calls), not a config object — one thing to learn,
// not a routing DSL.
//
// AddRoute registers an already-constructed Component; AddLazyRoute
// registers a loader instead, for real code-splitting (see PageProvider/
// LazyPage below) — but neither is stored as a raw function value in an
// array (`Component[]`/`(() => Component)[]`): KopScript's type grammar
// has no way to write "array of function type" in v1 (a parenthesized
// function type isn't a general grouping construct), so both cases are
// wrapped in a PageProvider instance instead, and it's `PageProvider[]`
// that's actually stored. Either way, once a page is built (eagerly at
// registration, or on first navigation for a lazy one) it's kept alive
// for the Router's own lifetime, so its own state<T> fields survive
// navigating away and back — no state gets reset just because a route
// wasn't showing for a while.
//
// No nullable types in v1 means "no route matched" can't be represented as
// null — a `NotFoundPage` is required up front instead, the same way a
// `match` expression requires its own `_` wildcard arm.
// A registered route's page, either already built (EagerPage — every
// route registered via the original AddRoute) or not fetched yet
// (LazyPage — AddLazyRoute, real code-splitting: the page's own JS chunk
// is only ever requested the first time its route actually matches, not
// eagerly with everything else at startup). Router itself only ever
// touches a route through this interface, never caring which case it is.
interface PageProvider {
  // Synchronous: the resolved Component if one is already available (an
  // EagerPage always has one; a LazyPage does once its own Resolve() has
  // completed at least once), or null if not yet loaded. Router's own
  // Render() calls this on every render to decide whether to mount the
  // real page or show a loading placeholder — never triggers a fetch by
  // itself.
  Component? Peek();

  // Produces the resolved Component, fetching it if this is the first
  // call (a LazyPage calls its own loader exactly once, ever, caching the
  // result — every later call, on this or a repeat navigation, returns
  // the SAME cached instance, preserving the same "a page stays alive and
  // keeps its own state across navigations" guarantee eager pages already
  // have). An EagerPage already has its Component, so this just returns
  // it immediately.
  task<Component> Resolve();
}

class EagerPage : PageProvider {
  private Component ThePage;
  constructor(Component page) { this.ThePage = page; }
  public Component? Peek() { return this.ThePage; }
  public async task<Component> Resolve() { return this.ThePage; }
}

class LazyPage : PageProvider {
  private () => task<Component> Loader;
  private Component? CachedValue;

  constructor(() => task<Component> loader) {
    this.Loader = loader;
    this.CachedValue = null;
  }

  public Component? Peek() {
    return this.CachedValue;
  }

  public async task<Component> Resolve() {
    Component? maybeCached = this.CachedValue;
    if (maybeCached != null) {
      Component cached = maybeCached;
      return cached;
    }
    Component loaded = await this.Loader();
    this.CachedValue = loaded;
    return loaded;
  }
}

class Router : Component {
  private string[] Paths;
  private PageProvider[] Pages;
  private PageProvider NotFoundProvider;
  // Tracks whichever PageProvider is currently being Resolve()d, if any —
  // compared by reference identity so navigating to a SECOND lazy route
  // while a FIRST one is still loading starts loading the second instead
  // of getting stuck waiting on a load nothing will ever re-render for
  // (Peek() will never return non-null for the route actually showing).
  // Reset to null once that specific load finishes.
  private PageProvider? LoadingProvider;

  // The FIRST captured dynamic segment's value (e.g. "42" for a
  // "/dogs/:id" route matching "/dogs/42"), or "" if the matched route has
  // none — kept exactly as-is (a plain string, not the general Params()
  // lookup below) so every existing single-dynamic-segment consumer keeps
  // working unchanged. For a route with more than one captured segment,
  // use Params(name) instead; this still holds the first one either way.
  //
  // A plain field, deliberately **not** state<T> — Render() below always
  // rebuilds a fresh outlet and re-Mount()s the matched page into it on
  // every Navigate()/popstate, which already re-runs that page's own
  // Render() (reading the fresh Param) with no extra step. Subscribing to
  // it, the way a page reacts to its *own* state<T>, would fire during
  // Match() itself — before Render() has finished swapping the matched page
  // in — which crashes the very first time a route no page has been
  // Mount()ed into yet matches, since a not-yet-mounted Component's
  // inherited Update() has no ParentElement to replaceChild into.
  public string Param;

  // Every named dynamic segment captured by whatever route just matched
  // (":id"/":toyId" for "/dogs/:id/toys/:toyId"), plus the pseudo-name "*"
  // for a trailing wildcard segment's captured remainder — see Params()
  // below. Parallel arrays (ParamNames[i] <-> ParamValues[i]), the same
  // convention Paths/Pages above already use — not a Dictionary type,
  // since KopScript has none.
  private string[] ParamNames;
  private string[] ParamValues;

  // The current URL's query string, parsed once per navigation/popstate —
  // see Query() below. Never part of route *matching* itself in v1: every
  // route sees whatever query string is actually in the URL, matched or
  // not. Values are NOT percent-decoded (a deliberate v1 cut — no
  // decodeURIComponent binding exists yet); a value containing `%20` or
  // `+` for a space arrives exactly as written in the URL.
  private string[] QueryKeys;
  private string[] QueryValues;

  // Checked before every navigation, including a direct load/refresh — not
  // just Navigate()/popstate. Deliberately one guard for the whole Router,
  // not per-route: the guard function itself decides which paths it cares
  // about (typically `path.StartsWith("/admin")`-style checks), the same
  // "no config object, just a function" style Http/DI already use, rather
  // than a parallel array of per-route guards (which also can't be written
  // as a type anyway — see AddRoute's own comment on array-of-function
  // types). Defaults to always-allow, not null — a nullable function TYPE
  // has the same "can't parenthesize for a postfix `?`" problem as an
  // array of one, so "no guard configured" is a real function that always
  // returns true, not a null check.
  private (string) => bool Guard;
  private string RedirectPath;

  constructor(Component notFoundPage) : base() {
    this.Paths = [];
    this.Pages = [];
    this.NotFoundProvider = new EagerPage(notFoundPage);
    this.LoadingProvider = null;
    this.Param = "";
    this.ParamNames = [];
    this.ParamValues = [];
    this.QueryKeys = [];
    this.QueryValues = [];
    this.Guard = (string path) => true;
    this.RedirectPath = "";
    // 'popstate' only fires on browser back/forward (or history.go/back/
    // forward) — never on pushState itself, unlike hashchange firing
    // whenever location.hash is set. Navigate() below calls Update()
    // directly for that reason; this listener only covers the back/forward
    // case, where the URL changes without any of our own code running.
    window.addEventListener("popstate", (Event e) => this.Update());
  }

  // A path segment written ":name" (e.g. "dogs/:id") matches any single
  // non-empty segment; every other segment must match literally, except a
  // trailing "*" segment (e.g. "files/*"), which matches one or more
  // remaining segments as a single captured group (see Params("*")).
  // Comparing segment-by-segment (rather than the whole string at once) is
  // what makes a static route's own matching still exactly as strict as a
  // plain `==` was before — same segment count, same literal text
  // throughout (a trailing "*" route aside, which only ever needs *at
  // least* as many path segments as its static prefix).
  public void AddRoute(string path, Component page) {
    this.Paths = this.Paths.Push(path);
    this.Pages = this.Pages.Push(new EagerPage(page));
  }

  // Real code-splitting: `loader` is a hand-written JS shim's dynamic
  // `import()`, reached via `extern task<Component> Load...() from
  // "./loader";` (see kopscript's own LLM.md/README.md "extern" section)
  // — its target module is only ever fetched the first time this route
  // actually matches, not eagerly with every other page at startup. The
  // loaded page still stays alive across navigations exactly like an
  // eager one does (LazyPage caches it after the first Resolve()); only
  // the FIRST time this route is visited pays the fetch cost.
  public void AddLazyRoute(string path, () => task<Component> loader) {
    this.Paths = this.Paths.Push(path);
    this.Pages = this.Pages.Push(new LazyPage(loader));
  }

  // Every registered path, in `AddRoute`/`AddLazyRoute` registration order
  // — for a caller that needs to enumerate real routes instead of hand-
  // maintaining a second, driftable list (a build-time static-prerender
  // step, most likely). A copy, not the live backing array — Paths is
  // never mutated after registration in practice, but returning the field
  // directly would let a caller do so anyway.
  public string[] AllPaths() {
    return this.Paths.Slice(0, this.Paths.Length);
  }

  // `guard` is called with the path being navigated to; returning false
  // redirects to `redirectPath` instead (updating the URL via pushState, so
  // a refresh on the blocked path lands on the redirect too, not back on
  // the page the guard just rejected). `redirectPath` itself is never
  // guard-checked — pick one the guard always allows, or it'll loop.
  public void SetGuard(string redirectPath, (string) => bool guard) {
    this.RedirectPath = redirectPath;
    this.Guard = guard;
  }

  public void Navigate(string path) {
    history.pushState("", "", path);
    this.Update();
  }

  // The captured value for a named dynamic segment (e.g. "toyId" for
  // ":toyId" in "/dogs/:id/toys/:toyId") or "*" for a trailing wildcard
  // segment's matched remainder — "" if `name` wasn't captured by whatever
  // route just matched, the same "empty string, not null" convention
  // `Param` itself already uses.
  public string Params(string name) {
    for (number i = 0; i < this.ParamNames.Length; i = i + 1) {
      if (this.ParamNames[i] == name) {
        return this.ParamValues[i];
      }
    }
    return "";
  }

  // The current URL's query-string value for `key` (e.g. "name" for
  // "?sort=name") — "" if `key` isn't present. See QueryKeys/QueryValues'
  // own comment for the percent-decoding cut.
  public string Query(string key) {
    for (number i = 0; i < this.QueryKeys.Length; i = i + 1) {
      if (this.QueryKeys[i] == key) {
        return this.QueryValues[i];
      }
    }
    return "";
  }

  // KopScript strings have no index-based substring/slice method (only
  // arrays do — see Array.Slice), so splitting on the literal separator and
  // taking the piece after it is the idiomatic way to strip a known
  // single-character prefix here, both for "?" (query string) and ":"
  // (a pattern segment's own leading marker, used below in Match).
  private void ParseQuery(string pathWithQuery) {
    this.QueryKeys = [];
    this.QueryValues = [];
    string[] afterMark = pathWithQuery.Split("?");
    if (afterMark.Length < 2) { return; }
    string rawQuery = afterMark[1];
    if (rawQuery.Length == 0) { return; }
    string[] pairs = rawQuery.Split("&");
    foreach (string pair in pairs) {
      string[] kv = pair.Split("=");
      if (kv.Length == 2) {
        this.QueryKeys = this.QueryKeys.Push(kv[0]);
        this.QueryValues = this.QueryValues.Push(kv[1]);
      }
    }
  }

  // A trailing slash is the same route as without one (`/about/` ==
  // `/about`) — every mainstream router normalizes this, and real
  // real-world links/directory-style static hosting (a build-time
  // prerendered route's own `/about/index.html`, most concretely) produce
  // trailing-slash URLs routinely. `"/"` itself is left alone — stripping
  // its own trailing slash would leave an empty string, changing what
  // matches the root route rather than normalizing it. Strings have no
  // substring/slice method (see kopscript's own LLM.md "Does not exist"),
  // so this rebuilds via the array methods that do exist instead of
  // trimming the string directly.
  private string NormalizePath(string path) {
    if (path == "/" || !path.EndsWith("/")) {
      return path;
    }
    string[] segments = path.Split("/");
    return segments.Slice(0, segments.Length - 1).Join("/");
  }

  private PageProvider Match(string rawFullPath) {
    string fullPath = this.NormalizePath(rawFullPath);
    string effectivePath = fullPath;
    if (!this.Guard(fullPath)) {
      history.pushState("", "", this.RedirectPath);
      effectivePath = this.RedirectPath;
    }

    this.ParseQuery(effectivePath);
    string[] pathAndQuery = effectivePath.Split("?");
    string[] pathSegments = pathAndQuery[0].Split("/");

    PageProvider found = this.NotFoundProvider;
    string[] foundNames = [];
    string[] foundValues = [];

    for (number i = 0; i < this.Paths.Length; i = i + 1) {
      string[] patternSegments = this.Paths[i].Split("/");
      bool hasWildcard = patternSegments.Length > 0 && patternSegments[patternSegments.Length - 1] == "*";

      number staticCount = patternSegments.Length;
      if (hasWildcard) {
        staticCount = patternSegments.Length - 1;
      }

      if (hasWildcard) {
        if (pathSegments.Length < staticCount) { continue; }
      } else {
        if (patternSegments.Length != pathSegments.Length) { continue; }
      }

      bool matched = true;
      string[] names = [];
      string[] values = [];
      for (number j = 0; j < staticCount; j = j + 1) {
        if (patternSegments[j].StartsWith(":")) {
          names = names.Push(patternSegments[j].Split(":")[1]);
          values = values.Push(pathSegments[j]);
        } else if (patternSegments[j] != pathSegments[j]) {
          matched = false;
          break;
        }
      }

      if (matched && hasWildcard) {
        names = names.Push("*");
        values = values.Push(pathSegments.Slice(staticCount, pathSegments.Length).Join("/"));
      }

      if (matched) {
        found = this.Pages[i];
        foundNames = names;
        foundValues = values;
        break;
      }
    }

    this.ParamNames = foundNames;
    this.ParamValues = foundValues;
    this.Param = "";
    if (foundValues.Length > 0) {
      this.Param = foundValues[0];
    }
    return found;
  }

  // The outlet itself is a plain, unchanging `<div>` — its tag/className
  // never change between navigations, so Component's own diffing (see
  // vdom.ks) reuses the exact same real outlet element across every
  // navigation, rather than rebuilding it. What goes *inside* it (the
  // matched page) is embedded via VElement.Mount — a single child, so it
  // needs no .Id (there's never more than one to key against; positional
  // matching is unambiguous). Navigating to a DIFFERENT page than last
  // time is a different Mountable instance in the slot, so Patch() tears
  // the old one down (Teardown() — its OnUnmount fires, IsMounted resets)
  // and mounts the new one fresh — matching this class's original
  // behavior exactly (every navigation always freshly Mount()s the
  // matched page; a page's own re-renders, once mounted, still go through
  // the normal diffed Update() path when its own state changes). The one
  // real change from before: a page navigated AWAY from now actually gets
  // torn down (OnUnmount fires) instead of being silently abandoned with
  // its DOM ripped out from under it and IsMounted left permanently true.
  public override VElement Render() {
    string path = location.pathname + location.search;
    PageProvider provider = this.Match(path);

    VElement outlet = VElement.Create("div");
    outlet.ClassName = "router-outlet";

    Component? maybeResolved = provider.Peek();
    if (maybeResolved != null) {
      Component resolved = maybeResolved;
      outlet.AppendChild(VElement.Mount(resolved));
    } else {
      bool alreadyLoadingThisProvider = false;
      PageProvider? maybeLoadingProvider = this.LoadingProvider;
      if (maybeLoadingProvider != null) {
        PageProvider loadingProvider = maybeLoadingProvider;
        if (loadingProvider == provider) {
          alreadyLoadingThisProvider = true;
        }
      }
      if (!alreadyLoadingThisProvider) {
        this.LoadingProvider = provider;
        this.LoadThenUpdate(provider);
      }
      outlet.AppendChild(this.BuildLoadingPlaceholder());
    }
    return outlet;
  }

  // Fire-and-forget from Render()'s own point of view — there's no way to
  // construct a task value outside an async function body to await it
  // there instead (see kopscript's own LLM.md "Async" docs), the same
  // reason Component's own OnUnmount-cascading Teardown() isn't awaited
  // by its own caller either. Calls this.Update() once the fetch actually
  // lands, so Render() runs again and this time provider.Peek() returns
  // the now-cached page instead of null.
  private async task LoadThenUpdate(PageProvider provider) {
    await provider.Resolve();
    this.LoadingProvider = null;
    this.Update();
  }

  // Shown in the outlet while a lazily-loaded route's own page is still
  // being fetched (AddLazyRoute) — never shown for an eager route, which
  // always has a Component to Peek() immediately. Override to customize;
  // default is a plain, unstyled placeholder so a real app always sees
  // SOMETHING rather than a silently blank outlet while a chunk loads.
  protected virtual VElement BuildLoadingPlaceholder() {
    VElement div = VElement.Create("div");
    div.ClassName = "router-loading";
    div.TextContent = "Loading...";
    return div;
  }
}
